/**
 * apps/web/src/components/AppLayout.tsx
 * -------------------------------------------------------
 * Purpose:
 *   Main authenticated layout (sidebar + header + content outlet).
 *
 * High-level data flow:
 *   - The parent route loader (router.tsx -> rootLoader) provides `me` data.
 *   - AppLayout renders navigation and shows the nested route via <Outlet/>.
 */

import React from 'react';
import {
  AppShell,
  Burger,
  Group,
  NavLink,
  ScrollArea,
  Text,
  UnstyledButton,
  Stack,
  Divider,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  Form,
  Link,
  Outlet,
  useLocation,
  useRouteLoaderData,
} from 'react-router-dom';
import {
  ClipboardCheck,
  LayoutDashboard,
  Inbox,
  TestTube,
  FileText,
  LogOut,
  Settings,
  ShieldCheck,
} from 'lucide-react';

import type { Me } from '@tinypro-audit/contracts';
import { APP_TIME_ZONE_LABEL } from '@tinypro-audit/shared/time';
import { TenantPoweredByStack } from './TenantPoweredByStack';
import { InboxCount } from './InboxCount';

/** Route loader data shape returned by rootLoader(). */
type RootLoaderData = { me: Me };

/** Navigation items shown in the sidebar. */
const primaryNavItems = [
  { to: '/inbox', label: 'Inbox', icon: Inbox },
  { to: '/projects', label: 'Projects', icon: ClipboardCheck },
  { to: '/', label: 'Analytics', icon: LayoutDashboard },
] as const;

const baseAdvancedNavItems = [
  { to: '/backtests', label: 'Backtests', icon: TestTube },
  { to: '/prompts', label: 'Prompts', icon: FileText },
] as const;

export function AppLayout(): React.ReactElement {
  /** Current location used to highlight the active nav item. */
  const location = useLocation();

  /** Root route data (user/org/role). */
  const data = useRouteLoaderData('root') as RootLoaderData;

  /** Advanced items available to all authenticated users. */
  const advancedNavItems = baseAdvancedNavItems;

  /** Admin-only items shown only to org admins. */
  const adminNavItems =
    data.me.role === 'admin'
      ? [
          {
            to: '/admin/resolve-activity',
            label: 'Resolve activity',
            icon: ClipboardCheck,
          },
          { to: '/admin/api-keys', label: 'API keys', icon: ShieldCheck },
          {
            to: '/admin/review-policy',
            label: 'Review policies',
            icon: Settings,
          },
        ]
      : [];

  /** Sidebar open/close state for mobile viewports. */
  const [mobileOpened, mobileHandlers] = useDisclosure(false);

  const isActive = (to: string) =>
    location.pathname === to ||
    location.pathname.startsWith(`${to}/`) ||
    (to === '/projects' && location.pathname.startsWith('/audits'));
  const advancedActive = advancedNavItems.some((item) => isActive(item.to));
  const adminActive = adminNavItems.some((item) => isActive(item.to));

  return (
    <AppShell
      header={{ height: 84 }}
      navbar={{
        width: 280,
        breakpoint: 'sm',
        collapsed: { mobile: !mobileOpened },
      }}
      padding="md"
    >
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between">
          <Group gap="sm" wrap="nowrap">
            <Burger
              opened={mobileOpened}
              onClick={mobileHandlers.toggle}
              hiddenFrom="sm"
              size="sm"
            />
            <UnstyledButton
              component={Link}
              to="/"
              aria-label="Go to dashboard"
              onClick={mobileHandlers.close}
              style={{ color: 'inherit' }}
            >
              <TenantPoweredByStack
                tenantName={data.me.org.name}
                tenantLogoUrl={data.me.org.branding.logoUrl}
                tenantLogoAltText={data.me.org.branding.logoAltText}
              />
            </UnstyledButton>
          </Group>

          <Group gap="xs">
            {/* Help & notifications reserved for future implementation */}

            <Form method="post" action="/logout">
              <UnstyledButton aria-label="Sign out" type="submit">
                <LogOut size={20} />
              </UnstyledButton>
            </Form>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="md">
        <AppShell.Section component={ScrollArea} grow>
          <Stack gap={4}>
            {primaryNavItems.map((item) => (
              <NavLink
                key={item.to}
                href={item.to}
                label={item.label}
                leftSection={<item.icon size={18} />}
                active={isActive(item.to)}
                rightSection={
                  item.to === '/inbox' ? (
                    <InboxCount identity={`${data.me.org.id}:${data.me.user.id}`} />
                  ) : undefined
                }
              />
            ))}

            {adminNavItems.length > 0 ? (
              <NavLink
                label="Admin"
                leftSection={<ShieldCheck size={18} />}
                defaultOpened={adminActive}
                active={adminActive}
              >
                {adminNavItems.map((item) => (
                  <NavLink
                    key={item.to}
                    href={item.to}
                    label={item.label}
                    leftSection={<item.icon size={18} />}
                    active={isActive(item.to)}
                  />
                ))}
              </NavLink>
            ) : null}

            <NavLink
              label="Advanced"
              leftSection={<Settings size={18} />}
              defaultOpened={advancedActive}
              active={advancedActive}
            >
              {advancedNavItems.map((item) => (
                <NavLink
                  key={item.to}
                  href={item.to}
                  label={item.label}
                  leftSection={<item.icon size={18} />}
                  active={isActive(item.to)}
                />
              ))}
            </NavLink>
          </Stack>
        </AppShell.Section>

        <AppShell.Section mt="md">
          <Divider my="sm" />

          <Text size="xs" c="dimmed">
            Signed in as
          </Text>
          <Text fw={600} size="sm">
            {data.me.user.email}
          </Text>
          <Text size="xs" c="dimmed" mt={4}>
            Org: {data.me.org.name} • Role: {data.me.role}
          </Text>
          <Text size="xs" c="dimmed" mt={2}>
            Time zone: {APP_TIME_ZONE_LABEL}
          </Text>
        </AppShell.Section>
      </AppShell.Navbar>

      <AppShell.Main>
        <Outlet />
      </AppShell.Main>
    </AppShell>
  );
}
